import React, { useState } from 'react';
import { getBestProfilePicture, isValidProfilePictureUrl } from '../utils/profilePicture';

const Avatar = ({ 
  src, 
  alt, 
  className = '', 
  size = 40,
  fallbackText = '',
  user = null, // Pass user object for better profile picture handling
  ...props 
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // Get the best available profile picture URL
  const profilePictureUrl = user ? getBestProfilePicture(user, size) : src;

  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  // Generate initials from alt text or fallback text
  const getInitials = (text) => {
    if (!text) return '?';
    return text
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Generate a consistent color based on the text
  const getBackgroundColor = (text) => {
    if (!text) return '#9CA3AF';
    
    const colors = [
      '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16',
      '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9',
      '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF',
      '#EC4899', '#F43F5E'
    ];
    
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  const shouldShowFallback = imageError || !profilePictureUrl || profilePictureUrl.trim() === '';
  const displayText = alt || fallbackText || (user && user.displayName) || '';

  if (shouldShowFallback) {
    return (
      <div
        className={`avatar-fallback ${className}`}
        style={{
          width: size,
          height: size,
          backgroundColor: getBackgroundColor(displayText),
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          fontSize: size * 0.4,
          fontWeight: '600',
          flexShrink: 0,
          userSelect: 'none'
        }}
        title={displayText}
        {...props}
      >
        {getInitials(displayText)}
      </div>
    );
  }

  return (
    <img
      src={profilePictureUrl}
      alt={alt || displayText}
      className={`avatar-image ${className} ${imageLoading ? 'loading' : ''}`}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        objectFit: 'cover',
        flexShrink: 0
      }}
      onError={handleImageError}
      onLoad={handleImageLoad}
      title={displayText}
      {...props}
    />
  );
};

export default Avatar;